/**
 * TripPlanMapPage — driver's read-only weekly trip plan with map.
 * Mirrors Flutter `company_driver/trip_plan_map_page.dart`.
 *
 *   Server: GET /api/cargo/me/company-driver/trip-plan
 *
 * Layout: weekly leg list on the right + map on the left. Tap a leg →
 * decode polyline + center map on it. The plan is set by admin; the
 * driver UI is purely informational.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Polyline, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  ArrowRight, Map as MapIcon, Loader, Calendar, Truck, Fuel, AlertCircle,
} from 'lucide-react';
import { useMyTripPlan } from '@/hooks/useTripPlan';
import { AppLayout } from '@/components/layout/AppLayout';
import type { TripPlanLeg } from '@/api/tripPlan';

const DAY_LABEL: Record<string, string> = {
  sat: 'السبت', sun: 'الأحد', mon: 'الاثنين', tue: 'الثلاثاء',
  wed: 'الأربعاء', thu: 'الخميس', fri: 'الجمعة',
};

const SYRIA_CENTER = { lat: 34.8021, lng: 38.9968 };

/**
 * Decode a Google encoded polyline string to lat/lng pairs.
 * Standard algorithm — `polyline.js` would also work, but inlining
 * keeps the bundle tighter for one-page usage.
 */
function decodePolyline(str: string): { lat: number; lng: number }[] {
  if (!str) return [];
  const points: { lat: number; lng: number }[] = [];
  let lat = 0, lng = 0, index = 0;
  while (index < str.length) {
    let result = 0, shift = 0, b: number;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;
    result = 0; shift = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

export default function TripPlanMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: plan, isLoading } = useMyTripPlan();
  const [selectedIdx, setSelectedIdx] = useState(0);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    id: 'trip-plan-map',
  });

  const legs: TripPlanLeg[] = plan?.plan_doc?.weekly_schedule || [];
  const selected = legs[selectedIdx] ?? null;
  const decodedPath = useMemo(() => selected?.polyline ? decodePolyline(selected.polyline) : [], [selected]);

  // Auto-select first leg when plan loads
  useEffect(() => {
    if (legs.length > 0 && selectedIdx >= legs.length) setSelectedIdx(0);
  }, [legs.length, selectedIdx]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!plan) {
    return (
      <AppLayout>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
          <ArrowRight size={18} /> {t('back', 'رجوع')}
        </button>
        <div className="bg-surface border border-border rounded-2xl p-10 text-center max-w-lg mx-auto">
          <MapIcon size={48} className="mx-auto text-text-tertiary mb-3" />
          <h2 className="text-lg font-bold text-text-primary mb-2">
            {t('no_trip_plan_title', 'لا توجد خطة رحلة بعد')}
          </h2>
          <p className="text-text-secondary text-sm">
            {t('no_trip_plan_desc', 'الإدارة لم تعيّن لك خطة أسبوعية بعد. تواصل مع شركتك إن كنت سائقاً تابعاً.')}
          </p>
        </div>
      </AppLayout>
    );
  }

  const center = decodedPath.length > 0 ? decodedPath[Math.floor(decodedPath.length / 2)] : SYRIA_CENTER;

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-6xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <MapIcon size={20} /> {t('trip_plan_title', 'خطة الرحلة الأسبوعية')}
        </h1>
        <p className="text-text-secondary text-sm mb-4">
          {plan.effective_to
            ? `${t('effective_from', 'سارية من')} ${new Date(plan.effective_from).toLocaleDateString('ar-SY')} ${t('to', 'حتى')} ${new Date(plan.effective_to).toLocaleDateString('ar-SY')}`
            : `${t('effective_from', 'سارية من')} ${new Date(plan.effective_from).toLocaleDateString('ar-SY')}`}
        </p>

        {plan.plan_doc.notes && (
          <div className="bg-info/5 border border-info/20 rounded-xl p-3 mb-4 text-sm text-text-primary">
            <strong className="block text-info mb-1">{t('admin_notes', 'ملاحظات من الإدارة')}:</strong>
            {plan.plan_doc.notes}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Schedule list */}
          <div className="lg:col-span-2 space-y-2">
            <h2 className="text-sm font-bold text-text-secondary mb-2">
              {t('weekly_schedule', 'الجدول الأسبوعي')}
              <span className="text-xs text-text-tertiary mr-2">({legs.length})</span>
            </h2>
            {legs.length === 0 ? (
              <div className="bg-surface border border-border rounded-xl p-4 text-center text-text-tertiary text-sm">
                {t('no_legs', 'لا أيام مجدوَلة في هذه الخطة.')}
              </div>
            ) : (
              legs.map((leg, idx) => (
                <LegCard
                  key={idx}
                  leg={leg}
                  active={idx === selectedIdx}
                  onClick={() => setSelectedIdx(idx)}
                />
              ))
            )}
          </div>

          {/* Map */}
          <div className="lg:col-span-3">
            <div className="bg-surface border border-border rounded-2xl overflow-hidden h-[400px] lg:h-[600px]">
              {!apiKey ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
                  <AlertCircle size={32} className="text-warning mb-2" />
                  <p className="text-sm text-text-secondary">
                    {t('maps_api_key_missing', 'مفتاح Google Maps غير مُهيأ. الخريطة معطلة، لكن الجدول أعلاه كافٍ للإطلاع.')}
                  </p>
                </div>
              ) : loadError ? (
                <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center">
                  <AlertCircle size={32} className="text-error mb-2" />
                  <p className="text-sm text-text-secondary">
                    {t('maps_load_error', 'فشل تحميل الخريطة. تحقق من اتصالك بالإنترنت.')}
                  </p>
                </div>
              ) : !isLoaded ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader size={28} className="animate-spin text-text-tertiary" />
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={center}
                  zoom={decodedPath.length > 0 ? 8 : 7}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  {decodedPath.length > 0 && (
                    <>
                      <Polyline
                        path={decodedPath}
                        options={{
                          strokeColor: '#0F2A5C',
                          strokeOpacity: 0.9,
                          strokeWeight: 5,
                        }}
                      />
                      <Marker position={decodedPath[0]} label="A" />
                      <Marker position={decodedPath[decodedPath.length - 1]} label="B" />
                    </>
                  )}
                </GoogleMap>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function LegCard({ leg, active, onClick }: { leg: TripPlanLeg; active: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  const stops = leg.transfer_points || [];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-right rounded-xl p-3 border-2 transition ${
        active
          ? 'border-primary bg-primary/5'
          : 'border-border bg-surface hover:border-primary/40'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-sm">
          <Calendar size={14} className="text-primary" />
          <span className="font-bold">{DAY_LABEL[leg.day] ?? leg.day}</span>
        </div>
        {leg.expected_km != null && (
          <span className="text-xs text-text-tertiary">
            <Truck size={11} className="inline ml-0.5" />
            {leg.expected_km} كم
          </span>
        )}
      </div>
      <div className="text-sm text-text-primary mb-1">
        <span className="font-semibold">{leg.from}</span>
        <span className="mx-1 text-text-tertiary">←</span>
        <span className="font-semibold">{leg.to}</span>
      </div>
      {stops.length > 0 && (
        <div className="text-xs text-text-secondary mb-1">
          <strong>{t('stops', 'محطات')}:</strong>{' '}
          {stops.map((s) => s.city).join(' · ')}
        </div>
      )}
      <div className="flex flex-wrap gap-2 text-xs text-text-tertiary">
        {leg.expected_liters != null && (
          <span><Fuel size={10} className="inline" /> {leg.expected_liters} L</span>
        )}
        {leg.notes && <span className="italic">— {leg.notes}</span>}
      </div>
    </button>
  );
}
